import { useCallback, useMemo } from 'react'
import { getExcludedFilterOptionIds } from './CommentsView.filters'
import {
    CommentsViewCommonFilterId,
    CommentsViewDateRangeFiltersState,
    CommentsViewDomainFilterId,
    CommentsViewDomainTabId,
    CommentsViewFiltersState,
    CommentsViewTabId,
} from './CommentsView.types'

type UseCommentsViewFiltersArgs = {
    filtersState: CommentsViewFiltersState
    updateFilters: (filtersState: CommentsViewFiltersState) => void
}

export type CommentsViewFilterHandlers = {
    onCommonFilterChange: (
        filterId: CommentsViewCommonFilterId,
        selectedOptionIds: string[],
        availableOptionIds: string[]
    ) => void
    onDateRangeChange: (field: keyof CommentsViewDateRangeFiltersState, value: string | null) => void
    onDomainFilterChange: (
        domainTabId: CommentsViewDomainTabId,
        filterId: CommentsViewDomainFilterId,
        selectedOptionIds: string[],
        availableOptionIds: string[]
    ) => void
    onResetFilters: (tabId: CommentsViewTabId) => void
    onResetGeneralFilters: () => void
    onResetTabDomainFilters: (domainTabId: CommentsViewDomainTabId) => void
}

export const useCommentsViewFilters = ({
    filtersState,
    updateFilters,
}: UseCommentsViewFiltersArgs): CommentsViewFilterHandlers => {
    const onCommonFilterChange = useCallback(
        (filterId: CommentsViewCommonFilterId, selectedOptionIds: string[], availableOptionIds: string[]) => {
            const availableOptions = availableOptionIds.map((optionId) => ({ id: optionId, label: optionId }))
            updateFilters({
                ...filtersState,
                common: {
                    ...filtersState.common,
                    [filterId]: getExcludedFilterOptionIds(availableOptions, selectedOptionIds),
                },
            })
        },
        [filtersState, updateFilters]
    )

    const onDateRangeChange = useCallback(
        (field: keyof CommentsViewDateRangeFiltersState, value: string | null) => {
            updateFilters({
                ...filtersState,
                dateRange: {
                    ...filtersState.dateRange,
                    [field]: value,
                },
            })
        },
        [filtersState, updateFilters]
    )

    const onDomainFilterChange = useCallback(
        (
            domainTabId: CommentsViewDomainTabId,
            filterId: CommentsViewDomainFilterId,
            selectedOptionIds: string[],
            availableOptionIds: string[]
        ) => {
            const availableOptions = availableOptionIds.map((optionId) => ({ id: optionId, label: optionId }))
            updateFilters({
                ...filtersState,
                domains: {
                    ...filtersState.domains,
                    [domainTabId]: {
                        ...(filtersState.domains[domainTabId] ?? {}),
                        [filterId]: getExcludedFilterOptionIds(availableOptions, selectedOptionIds),
                    },
                },
            })
        },
        [filtersState, updateFilters]
    )

    const onResetFilters = useCallback(
        (tabId: CommentsViewTabId) => {
            updateFilters({
                common: {},
                dateRange: { from: null, to: null },
                domains:
                    tabId === 'all'
                        ? {}
                        : {
                              ...filtersState.domains,
                              [tabId]: {},
                          },
            })
        },
        [filtersState.domains, updateFilters]
    )

    const onResetGeneralFilters = useCallback(() => {
        updateFilters({
            ...filtersState,
            common: {},
            dateRange: { from: null, to: null },
        })
    }, [filtersState, updateFilters])

    const onResetTabDomainFilters = useCallback(
        (domainTabId: CommentsViewDomainTabId) => {
            updateFilters({
                ...filtersState,
                domains: {
                    ...filtersState.domains,
                    [domainTabId]: {},
                },
            })
        },
        [filtersState, updateFilters]
    )

    return useMemo(
        () => ({
            onCommonFilterChange,
            onDateRangeChange,
            onDomainFilterChange,
            onResetFilters,
            onResetGeneralFilters,
            onResetTabDomainFilters,
        }),
        [
            onCommonFilterChange,
            onDateRangeChange,
            onDomainFilterChange,
            onResetFilters,
            onResetGeneralFilters,
            onResetTabDomainFilters,
        ]
    )
}
